import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, ArrowLeft, Star, TrendingUp, Users, Play, ExternalLink, Calendar, Package, Save, Bookmark, BookmarkCheck, Eye, Clock, CheckCircle, XCircle, AlertCircle, Download, RefreshCw, Code, FileText, GitBranch, Activity, Bell, Bug, Folder, Database } from "lucide-react";
import axiosInstance from "@/config/axios";
import { toast } from "sonner";
import ReactMarkdown from 'react-markdown';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ApifyActorDetailV2() {
  const { actorId } = useParams();
  const navigate = useNavigate();
  
  // Actor data
  const [actor, setActor] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Input management
  const [inputMode, setInputMode] = useState("manual"); // "manual" or "json"
  const [manualInputs, setManualInputs] = useState({});
  const [jsonInput, setJsonInput] = useState("{}");
  const [inputSchema, setInputSchema] = useState(null);
  const [isSaved, setIsSaved] = useState(false);
  
  // Runs
  const [runs, setRuns] = useState([]);
  const [loadingRuns, setLoadingRuns] = useState(false);
  const [currentRun, setCurrentRun] = useState(null);
  const [runResults, setRunResults] = useState(null);
  
  // Builds
  const [builds, setBuilds] = useState([]);
  const [selectedBuild, setSelectedBuild] = useState(null);
  
  // Saved Tasks
  const [savedTasks, setSavedTasks] = useState([]);
  const [saveTaskDialogOpen, setSaveTaskDialogOpen] = useState(false);
  const [taskName, setTaskName] = useState("");
  
  // Monitoring
  const [monitoring, setMonitoring] = useState({
    alerts: [],
    metrics: null
  });
  
  // Issues
  const [issues, setIssues] = useState([]);
  const [newIssueDialogOpen, setNewIssueDialogOpen] = useState(false);
  const [issueTitle, setIssueTitle] = useState("");
  const [issueDescription, setIssueDescription] = useState("");
  
  // README & Changelog
  const [readme, setReadme] = useState("");
  const [changelog, setChangelog] = useState("");
  
  // Execution
  const [running, setRunning] = useState(false);
  
  // Table column visibility
  const [visibleColumns, setVisibleColumns] = useState([]);
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [columnWidths, setColumnWidths] = useState({});
  const [defaultColumnWidth, setDefaultColumnWidth] = useState(200); // Default width in pixels
  
  // Pagination for performance
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  
  // Column ordering
  const [columnOrder, setColumnOrder] = useState([]);
  
  // Save to Leads
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [leadName, setLeadName] = useState("");
  const [savingLead, setSavingLead] = useState(false);
  const [runSavedInLeads, setRunSavedInLeads] = useState({});

  useEffect(() => {
    fetchActorDetails();
    fetchRuns();
    fetchBuilds();
    fetchSavedTasks();
    fetchIssues();
  }, [actorId]);

  // Initialize visible columns when results change
  useEffect(() => {
    if (runResults && runResults.items && runResults.items.length > 0) {
      const allKeys = new Set();
      runResults.items.forEach(item => {
        Object.keys(item).forEach(key => allKeys.add(key));
      });
      const allColumns = Array.from(allKeys);
      // Reset columns when new results are loaded
      setVisibleColumns(allColumns);
      setShowColumnSelector(false);
      setCurrentPage(1); // Reset to first page
      setColumnWidths({}); // Reset custom widths
      setColumnOrder(allColumns); // Initialize column order
    } else {
      setVisibleColumns([]);
    }
  }, [runResults]);

  const fetchActorDetails = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axiosInstance.get(`${API}/api/admin/apify/actors/${actorId}/details`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setActor(response.data);
      
      // Fetch input schema
      if (response.data.id) {
        fetchInputSchema(response.data.id);
      }
    } catch (error) {
      console.error("Error fetching actor details:", error);
      toast.error("Failed to fetch actor details");
    } finally {
      setLoading(false);
    }
  };

  const fetchInputSchema = async (actorFullId) => {
    try {
      const token = localStorage.getItem("token");
      console.log("Fetching input schema for:", actorFullId);
      const response = await axiosInstance.get(`${API}/api/admin/apify/actors/${actorFullId}/input-schema`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log("Input schema received:", response.data);
      setInputSchema(response.data);
      
      // Initialize manual inputs with default values from schema
      if (response.data?.properties) {
        const defaults = {};
        Object.keys(response.data.properties).forEach(key => {
          const prop = response.data.properties[key];
          if (prop.default !== undefined) {
            defaults[key] = prop.default;
          } else if (prop.type === 'array') {
            defaults[key] = prop.prefill || [];
          } else if (prop.type === 'boolean') {
            defaults[key] = prop.prefill || false;
          } else if (prop.type === 'string') {
            defaults[key] = prop.prefill || prop.example || '';
          } else if (prop.type === 'number' || prop.type === 'integer') {
            defaults[key] = prop.prefill || prop.default || 0;
          }
        });
        console.log("Initialized manual inputs:", defaults);
        setManualInputs(defaults);
        setJsonInput(JSON.stringify(defaults, null, 2));
      }
    } catch (error) {
      console.error("Error fetching input schema:", error);
      toast.error("Failed to load input schema");
    }
  };

  const fetchRuns = async () => {
    setLoadingRuns(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axiosInstance.get(`${API}/api/admin/apify/actors/${actorId}/runs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setRuns(response.data.runs || []);
    } catch (error) {
      console.error("Error fetching runs:", error);
    } finally {
      setLoadingRuns(false);
    }
  };

  const fetchBuilds = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axiosInstance.get(`${API}/api/admin/apify/actors/${actorId}/builds`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Sort builds by startedAt descending (latest first)
      const sortedBuilds = (response.data.builds || []).sort((a, b) => {
        return new Date(b.startedAt) - new Date(a.startedAt);
      });
      
      setBuilds(sortedBuilds);
      if (sortedBuilds.length > 0) {
        setSelectedBuild(sortedBuilds[0]);
      }
    } catch (error) {
      console.error("Error fetching builds:", error);
    }
  };

  const fetchSavedTasks = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axiosInstance.get(`${API}/api/admin/apify/actors/${actorId}/saved-tasks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSavedTasks(response.data.tasks || []);
    } catch (error) {
      console.error("Error fetching saved tasks:", error);
    }
  };

  const fetchIssues = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axiosInstance.get(`${API}/api/admin/apify/actors/${actorId}/issues`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setIssues(response.data.issues || []);
    } catch (error) {
      console.error("Error fetching issues:", error);
    }
  };

  const handleSaveInput = async () => {
    try {
      const token = localStorage.getItem("token");
      const inputData = inputMode === "json" ? JSON.parse(jsonInput) : manualInputs;
      
      await axiosInstance.post(
        `${API}/api/admin/apify/actors/${actorId}/save-input`,
        { input: inputData },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setIsSaved(true);
      toast.success("Input configuration saved!");
    } catch (error) {
      console.error("Error saving input:", error);
      toast.error("Failed to save input");
    }
  };

  const handleSaveAsTask = async () => {
    try {
      const token = localStorage.getItem("token");
      const inputData = inputMode === "json" ? JSON.parse(jsonInput) : manualInputs;
      
      await axiosInstance.post(
        `${API}/api/admin/apify/actors/${actorId}/tasks`,
        { name: taskName, input: inputData },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Task saved successfully!");
      setSaveTaskDialogOpen(false);
      setTaskName("");
      fetchSavedTasks();
    } catch (error) {
      console.error("Error saving task:", error);
      toast.error("Failed to save task");
    }
  };

  const handleRunTask = async (task) => {
    setManualInputs(task.input);
    setJsonInput(JSON.stringify(task.input, null, 2));
    toast.success(`Loaded task: ${task.name}`);
  };

  const handleCreateIssue = async () => {
    try {
      const token = localStorage.getItem("token");
      
      await axiosInstance.post(
        `${API}/api/admin/apify/actors/${actorId}/issues`,
        { title: issueTitle, description: issueDescription },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Issue created successfully!");
      setNewIssueDialogOpen(false);
      setIssueTitle("");
      setIssueDescription("");
      fetchIssues();
    } catch (error) {
      console.error("Error creating issue:", error);
      toast.error("Failed to create issue");
    }
  };

  const handleDownloadResults = async (runId, format) => {
    if (!runId) {
      toast.error("No run selected. Please view results first.");
      return;
    }

    if (!runResults || !runResults.items || runResults.items.length === 0) {
      toast.error("No results to download.");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      
      // For JSON format, create the file directly from runResults
      if (format === 'json') {
        const dataStr = JSON.stringify(runResults.items, null, 2);
        const blob = new Blob([dataStr], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `results-${runId}.json`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toast.success('Downloaded as JSON');
        return;
      }

      // For CSV format, convert to CSV
      if (format === 'csv') {
        const items = runResults.items;
        if (items.length === 0) {
          toast.error("No data to convert to CSV");
          return;
        }

        // Get all unique keys
        const allKeys = new Set();
        items.forEach(item => {
          Object.keys(item).forEach(key => allKeys.add(key));
        });
        const headers = Array.from(allKeys);

        // Create CSV content
        let csvContent = headers.join(',') + '\n';
        items.forEach(item => {
          const row = headers.map(header => {
            const value = item[header];
            if (value === null || value === undefined) return '';
            if (typeof value === 'object') return JSON.stringify(value).replace(/"/g, '""');
            return String(value).replace(/"/g, '""');
          });
          csvContent += row.map(v => `"${v}"`).join(',') + '\n';
        });

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `results-${runId}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toast.success('Downloaded as CSV');
        return;
      }

      toast.error('Unsupported format');
    } catch (error) {
      console.error("Error downloading results:", error);
      toast.error("Failed to download results: " + (error.message || "Unknown error"));
    }
  };

  const handleRunActor = async () => {
    setRunning(true);
    try {
      const token = localStorage.getItem("token");
      let inputData = inputMode === "json" ? JSON.parse(jsonInput) : { ...manualInputs };
      
      // Validate and clean input data based on schema
      if (inputSchema && inputSchema.properties) {
        Object.keys(inputSchema.properties).forEach(key => {
          const prop = inputSchema.properties[key];
          const value = inputData[key];
          const allowEmpty = prop.description && prop.description.toLowerCase().includes('leave empty');
          
          // Handle number/integer fields with minimum constraints
          if ((prop.type === 'number' || prop.type === 'integer')) {
            // If field allows empty and value is null/undefined, remove it (Apify will use default behavior)
            if (allowEmpty && (value === null || value === undefined || value === '')) {
              delete inputData[key];
            } else if (value !== undefined && value !== null && value !== '') {
              const numValue = Number(value);
              
              // If value is NaN or less than minimum (and minimum is required), use default or minimum
              if (isNaN(numValue)) {
                if (prop.default !== undefined) {
                  inputData[key] = prop.default;
                } else if (!allowEmpty && prop.minimum !== undefined) {
                  inputData[key] = prop.minimum;
                } else {
                  delete inputData[key];
                }
              } else if (!allowEmpty && prop.minimum !== undefined && numValue < prop.minimum) {
                inputData[key] = prop.minimum;
              }
            }
          }
          
          // Remove empty arrays
          if (Array.isArray(value) && value.length === 0) {
            delete inputData[key];
          }
          
          // Remove undefined or empty string values
          if (value === undefined || value === '') {
            delete inputData[key];
          }
        });
      }
      
      const response = await axiosInstance.post(
        `${API}/api/admin/apify/actors/${actorId}/run`,
        inputData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Actor run started! Run ID: ${response.data.run_id}`);
      setCurrentRun(response.data.run_id);
      
      // Refresh runs list
      setTimeout(() => fetchRuns(), 2000);
      
      // Poll for run status
      pollRunStatus(response.data.run_id);
    } catch (error) {
      console.error("Error running actor:", error);
      toast.error(error.response?.data?.detail || "Failed to run actor");
    } finally {
      setRunning(false);
    }
  };

  const pollRunStatus = async (runId) => {
    const token = localStorage.getItem("token");
    const maxAttempts = 60; // Poll for 5 minutes max
    let attempts = 0;

    const poll = async () => {
      try {
        const response = await axiosInstance.get(`${API}/api/admin/apify/runs/${runId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        const status = response.data.status;
        
        if (status === "SUCCEEDED" || status === "FAILED" || status === "ABORTED") {
          toast.success(`Run ${status.toLowerCase()}!`);
          fetchRuns();
          
          if (status === "SUCCEEDED") {
            fetchRunResults(runId);
          }
          return;
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 5000); // Poll every 5 seconds
        }
      } catch (error) {
        console.error("Error polling run status:", error);
      }
    };

    poll();
  };

  const renderResultsAsTable = (items) => {
    if (!items || items.length === 0) {
      return <div className="text-center py-8 text-slate-500">No results found</div>;
    }

    // Get all unique keys from all items
    const allKeys = new Set();
    items.forEach(item => {
      if (item && typeof item === 'object') {
        Object.keys(item).forEach(key => allKeys.add(key));
      }
    });
    
    const allColumns = Array.from(allKeys);
    
    if (allColumns.length === 0) {
      return <div className="text-center py-8 text-slate-500">No data to display</div>;
    }

    // Filter keys based on visible columns
    let keys = visibleColumns.length > 0 
      ? allColumns.filter(key => visibleColumns.includes(key))
      : allColumns;
    
    // Apply column ordering
    if (columnOrder.length > 0) {
      keys = keys.sort((a, b) => {
        const indexA = columnOrder.indexOf(a);
        const indexB = columnOrder.indexOf(b);
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;
        return indexA - indexB;
      });
    }
    
    if (keys.length === 0) {
      return (
        <div className="text-center py-8 text-slate-500">
          No columns selected. Please select at least one column to display.
        </div>
      );
    }

    // Pagination
    const totalPages = Math.ceil(items.length / rowsPerPage);
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = Math.min(startIndex + rowsPerPage, items.length);
    const paginatedItems = items.slice(startIndex, endIndex);

    // Toggle column visibility
    const toggleColumn = (column) => {
      setVisibleColumns(prev => 
        prev.includes(column) 
          ? prev.filter(c => c !== column)
          : [...prev, column]
      );
    };

    // Select/Deselect all columns
    const toggleAllColumns = () => {
      if (visibleColumns.length === allColumns.length) {
        setVisibleColumns([]);
      } else {
        setVisibleColumns(allColumns);
      }
    };

    // Helper function to calculate average from review distribution
    const calculateAverageRating = (reviewDist) => {
      if (!reviewDist || typeof reviewDist !== 'object') return null;
      
      const ratings = {
        oneStar: reviewDist.oneStar || 0,
        twoStar: reviewDist.twoStar || 0,
        threeStar: reviewDist.threeStar || 0,
        fourStar: reviewDist.fourStar || 0,
        fiveStar: reviewDist.fiveStar || 0
      };
      
      const totalReviews = Object.values(ratings).reduce((a, b) => a + b, 0);
      if (totalReviews === 0) return null;
      
      const weightedSum = ratings.oneStar * 1 + ratings.twoStar * 2 + ratings.threeStar * 3 + ratings.fourStar * 4 + ratings.fiveStar * 5;
      return (weightedSum / totalReviews).toFixed(1);
    };

    // Helper function to format tooltip content (presentable, not JSON)
    const formatTooltipContent = (value, columnName) => {
      if (value === null || value === undefined || value === '') return '-';
      
      // Handle objects
      if (typeof value === 'object') {
        // Opening hours
        if (columnName && columnName.toLowerCase().includes('opening') && columnName.toLowerCase().includes('hour')) {
          const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
          return days.map(day => {
            const hours = value[day];
            if (!hours) return null;
            return `${day.charAt(0).toUpperCase() + day.slice(1)}: ${hours}`;
          }).filter(Boolean).join('\n');
        }
        
        // People also search (array of objects with query)
        if (Array.isArray(value)) {
          if (value.length === 0) return 'No items';
          
          // Array of objects
          if (typeof value[0] === 'object') {
            return value.map((item, idx) => {
              const name = item.query || item.question || item.name || item.title || item.text;
              if (name) return `${idx + 1}. ${name}`;
              return `${idx + 1}. ${Object.values(item)[0]}`;
            }).join('\n');
          }
          
          // Array of primitives
          return value.join(', ');
        }
        
        // Generic objects - format as key: value pairs
        return Object.entries(value).map(([key, val]) => {
          if (typeof val === 'object') return `${key}: [Object]`;
          return `${key}: ${val}`;
        }).join('\n');
      }
      
      return String(value);
    };

    // Helper function to render cell value
    const renderCellValue = (value, columnName) => {
      // Handle null/undefined/empty
      if (value === null || value === undefined || value === '') {
        return <span title="-">-</span>;
      }
      
      // Handle booleans
      if (typeof value === 'boolean') {
        return <span title={value ? 'Yes' : 'No'}>{value ? 'Yes' : 'No'}</span>;
      }
      
      // Handle numbers
      if (typeof value === 'number') {
        const formatted = value.toLocaleString();
        return <span title={formatted}>{formatted}</span>;
      }
      
      // Get tooltip content
      const tooltipText = formatTooltipContent(value, columnName);
      
      // Handle objects and arrays
      if (typeof value === 'object') {
        // Special handling for review distribution
        if (columnName && columnName.toLowerCase().includes('review') && columnName.toLowerCase().includes('distribution')) {
          const avg = calculateAverageRating(value);
          if (avg) return <span title={tooltipText}>⭐ {avg} avg</span>;
          return <span title="-">-</span>;
        }
        
        // Handle arrays
        if (Array.isArray(value)) {
          if (value.length === 0) return <span title="No items">-</span>;
          
          // Check if array of primitives
          const firstItem = value[0];
          if (typeof firstItem !== 'object') {
            const display = value.length <= 3 ? value.join(', ') : `${value.slice(0, 2).join(', ')}... (+${value.length - 2} more)`;
            return <span title={tooltipText} className="cursor-help">{display}</span>;
          }
          
          // Array of objects - show count and first item preview
          const preview = firstItem.name || firstItem.title || firstItem.query || firstItem.question || Object.values(firstItem)[0];
          const display = preview ? String(preview).substring(0, 30) : 'Item 1';
          return (
            <span title={tooltipText} className="cursor-help">
              {display}... ({value.length} items)
            </span>
          );
        }
        
        // Handle complex objects (opening hours, Q&A, etc.)
        const objectKeys = Object.keys(value);
        if (objectKeys.length === 0) return <span title="Empty object">-</span>;
        
        // Create a readable summary
        const summary = objectKeys.slice(0, 2).map(key => {
          const val = value[key];
          if (typeof val === 'object') return `${key}`;
          return `${key}: ${String(val).substring(0, 15)}`;
        }).join(', ');
        
        return (
          <span title={tooltipText} className="cursor-help">
            {summary}{objectKeys.length > 2 ? '...' : ''}
          </span>
        );
      }
      
      // Check if value is a URL and make it clickable
      if (typeof value === 'string' && (value.startsWith('http://') || value.startsWith('https://'))) {
        return (
          <a 
            href={value} 
            target="_blank" 
            rel="noopener noreferrer" 
            className="text-blue-600 hover:underline"
            title={value}
          >
            {value.length > 40 ? value.substring(0, 40) + '...' : value}
          </a>
        );
      }
      
      // Handle strings - ALWAYS show tooltip
      const strValue = String(value);
      if (strValue.length > 50) {
        return (
          <span title={strValue} className="cursor-help">
            {strValue.substring(0, 50)}...
          </span>
        );
      }
      
      // Even short strings get tooltip
      return <span title={strValue} className="cursor-help">{strValue}</span>;
    };

    return (
      <div className="space-y-4">
        {/* Column Controls */}
        <div className="space-y-2">
          <div className="flex items-center justify-between bg-slate-50 p-3 rounded-lg border border-slate-200 gap-4">
            <div className="flex items-center gap-3 flex-wrap">
              <Button 
                size="sm" 
                variant="outline" 
                onClick={() => setShowColumnSelector(!showColumnSelector)}
              >
                <Eye className="w-4 h-4 mr-2" />
                Columns ({visibleColumns.length}/{allColumns.length})
              </Button>
            
            <div className="flex items-center gap-2">
              <Label className="text-xs text-slate-600 whitespace-nowrap">Width:</Label>
              <select
                value={defaultColumnWidth}
                onChange={(e) => setDefaultColumnWidth(Number(e.target.value))}
                className="h-8 text-xs border border-slate-300 rounded px-2"
              >
                <option value="150">Narrow (150px)</option>
                <option value="200">Normal (200px)</option>
                <option value="250">Wide (250px)</option>
                <option value="300">Extra Wide (300px)</option>
              </select>
            </div>
            
            <Button 
              size="sm" 
              variant="ghost"
              onClick={() => setColumnWidths({})}
              className="text-xs"
            >
              Reset
            </Button>
          </div>
          
              {showColumnSelector && (
                <Button size="sm" variant="ghost" onClick={toggleAllColumns}>
                  {visibleColumns.length === allColumns.length ? 'Deselect All' : 'Select All'}
                </Button>
              )}
            </div>
          
          <div className="text-xs text-slate-500 bg-blue-50 p-2 rounded border border-blue-200">
            💡 <strong>Tip:</strong> Drag column headers (⋮⋮) to reorder them. Hover over any cell to see full content.
          </div>
        </div>

        {/* Column Checkboxes */}
        {showColumnSelector && (
          <div className="bg-white p-4 rounded-lg border border-slate-200 max-h-48 overflow-y-auto">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {allColumns.map(column => (
                <label key={column} className="flex items-center gap-2 cursor-pointer hover:bg-slate-50 p-2 rounded">
                  <input
                    type="checkbox"
                    checked={visibleColumns.includes(column)}
                    onChange={() => toggleColumn(column)}
                    className="w-4 h-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                  />
                  <span className="text-sm text-slate-700">
                    {column.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Table Container with sticky header and visible scrollbar */}
        <style>{`
          .custom-scrollbar::-webkit-scrollbar {
            width: 12px;
            height: 12px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: #f1f5f9;
            border-radius: 6px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: #94a3b8;
            border-radius: 6px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: #64748b;
          }
          .custom-scrollbar {
            scrollbar-width: thin;
            scrollbar-color: #94a3b8 #f1f5f9;
          }
        `}</style>
        <div 
          className="custom-scrollbar relative border border-slate-200 rounded-lg"
          style={{ 
            maxHeight: '600px',
            overflowY: 'auto',
            overflowX: 'auto',
          }}
        >
          
          <table className="min-w-full divide-y divide-slate-200">
            {/* Sticky Header */}
            <thead className="bg-slate-100 sticky top-0 z-10 shadow-sm">
              <tr>
                <th 
                  className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider border-b border-slate-200 bg-slate-100 sticky left-0 z-20"
                  style={{ minWidth: '60px', width: '60px' }}
                >
                  #
                </th>
                {keys.map((key, index) => (
                  <th 
                    key={key} 
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.effectAllowed = 'move';
                      e.dataTransfer.setData('text/plain', key);
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = 'move';
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      const draggedKey = e.dataTransfer.getData('text/plain');
                      if (draggedKey === key) return;
                      
                      const newOrder = [...keys];
                      const draggedIndex = newOrder.indexOf(draggedKey);
                      const targetIndex = newOrder.indexOf(key);
                      
                      newOrder.splice(draggedIndex, 1);
                      newOrder.splice(targetIndex, 0, draggedKey);
                      
                      setColumnOrder(newOrder);
                    }}
                    className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider border-b border-slate-200 bg-slate-100 relative group cursor-move hover:bg-slate-200"
                    style={{ 
                      minWidth: `${columnWidths[key] || defaultColumnWidth}px`,
                      width: `${columnWidths[key] || defaultColumnWidth}px`,
                      maxWidth: `${columnWidths[key] || defaultColumnWidth}px`
                    }}
                  >
                    <span className="truncate flex items-center gap-1" title={key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}>
                      <span className="text-slate-400">⋮⋮</span>
                      {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200">
              {paginatedItems.map((item, idx) => (
                <tr key={startIndex + idx} className="hover:bg-slate-50 transition-colors">
                  <td 
                    className="px-4 py-3 text-sm text-slate-600 border-r border-slate-200 font-medium sticky left-0 bg-white z-10"
                    style={{ minWidth: '60px', width: '60px' }}
                  >
                    {startIndex + idx + 1}
                  </td>
                  {keys.map(key => (
                    <td 
                      key={key} 
                      className="px-4 py-3 text-sm text-slate-800 border-r border-slate-200"
                      style={{ 
                        minWidth: `${columnWidths[key] || defaultColumnWidth}px`,
                        width: `${columnWidths[key] || defaultColumnWidth}px`,
                        maxWidth: `${columnWidths[key] || defaultColumnWidth}px`,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {renderCellValue(item[key], key)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        <div className="flex items-center justify-between bg-slate-50 p-3 rounded-lg border border-slate-200 mt-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-600">
              Showing {startIndex + 1} to {endIndex} of {items.length} results
            </span>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
            >
              First
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </Button>
            <span className="text-sm text-slate-600 px-2">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              Next
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
            >
              Last
            </Button>
            
            <select
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="h-8 text-xs border border-slate-300 rounded px-2 ml-2"
            >
              <option value="25">25 rows</option>
              <option value="50">50 rows</option>
              <option value="100">100 rows</option>
              <option value="200">200 rows</option>
            </select>
          </div>
        </div>
      </div>
    );
  };

  const fetchRunResults = async (runId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axiosInstance.get(`${API}/api/admin/apify/runs/${runId}/dataset`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setRunResults(response.data);
      setCurrentRun(runId); // Set currentRun so download buttons work
      
      // Check if this run is already saved in leads
      checkRunInLeads(runId);
    } catch (error) {
      console.error("Error fetching run results:", error);
      toast.error("Failed to fetch run results");
    }
  };

  const checkRunInLeads = async (runId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axiosInstance.get(`${API}/api/admin/leads/check-run/${runId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRunSavedInLeads(prev => ({ ...prev, [runId]: response.data }));
    } catch (error) {
      console.error("Error checking run in leads:", error);
    }
  };

  const handleSaveToLeads = async () => {
    if (!leadName.trim()) {
      toast.error("Please enter a name for the lead list");
      return;
    }

    setSavingLead(true);
    try {
      const token = localStorage.getItem("token");
      
      const leadData = {
        name: leadName.trim(),
        actor_name: actor?.name || actor?.title || actorId,
        actor_id: actorId,
        run_id: currentRun,
        data: runResults.items,
        visible_columns: visibleColumns,
        column_order: columnOrder,
        default_column_width: defaultColumnWidth,
        scraped_at: new Date().toISOString()
      };

      await axiosInstance.post(`${API}/api/admin/leads`, leadData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`Saved to Leads as "${leadName}"`);
      setShowSaveDialog(false);
      setLeadName("");
      
      // Mark this run as saved
      setRunSavedInLeads(prev => ({ ...prev, [currentRun]: { saved: true, name: leadName } }));
    } catch (error) {
      console.error("Error saving to leads:", error);
      toast.error("Failed to save to leads");
    } finally {
      setSavingLead(false);
    }
  };

  const syncInputs = () => {
    if (inputMode === "json") {
      try {
        const parsed = JSON.parse(jsonInput);
        setManualInputs(parsed);
      } catch (e) {
        // Invalid JSON, don't sync
      }
    } else {
      setJsonInput(JSON.stringify(manualInputs, null, 2));
    }
  };

  const handleModeSwitch = (newMode) => {
    syncInputs();
    setInputMode(newMode);
  };

  const renderManualInput = (key, schema) => {
    const value = manualInputs[key];
    const type = schema.type;
    
    if (type === 'boolean') {
      return (
        <div key={key} className="flex items-center justify-between py-2">
          <div>
            <Label>{schema.title || key}</Label>
            {schema.description && (
              <p className="text-xs text-slate-500 mt-1">{schema.description}</p>
            )}
          </div>
          <Switch
            checked={value || false}
            onCheckedChange={(checked) => setManualInputs({ ...manualInputs, [key]: checked })}
          />
        </div>
      );
    }
    
    if (schema.enum) {
      return (
        <div key={key} className="space-y-2">
          <Label>{schema.title || key}</Label>
          {schema.description && (
            <p className="text-xs text-slate-500">{schema.description}</p>
          )}
          <Select
            value={value || ''}
            onValueChange={(val) => setManualInputs({ ...manualInputs, [key]: val })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select an option" />
            </SelectTrigger>
            <SelectContent>
              {schema.enum.map((option) => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );
    }
    
    if (type === 'array') {
      return (
        <div key={key} className="space-y-2">
          <Label>{schema.title || key}</Label>
          {schema.description && (
            <p className="text-xs text-slate-500">{schema.description}</p>
          )}
          <Textarea
            value={Array.isArray(value) ? value.join('\n') : ''}
            onChange={(e) => setManualInputs({ ...manualInputs, [key]: e.target.value.split('\n').filter(v => v) })}
            placeholder="One item per line"
            rows={4}
          />
        </div>
      );
    }
    
    if (type === 'number' || type === 'integer') {
      // Check if this field allows empty for "all results" behavior
      const allowEmpty = schema.description && schema.description.toLowerCase().includes('leave empty');
      
      return (
        <div key={key} className="space-y-2">
          <Label>{schema.title || key}</Label>
          {schema.description && (
            <p className="text-xs text-slate-500">{schema.description}</p>
          )}
          <Input
            type="number"
            value={value !== undefined && value !== null && value !== '' ? value : ''}
            onChange={(e) => {
              const val = e.target.value;
              
              // If empty and field allows empty, set to null (will be removed before submission)
              if (val === '' || val === null) {
                if (allowEmpty) {
                  setManualInputs({ ...manualInputs, [key]: null });
                } else {
                  const defaultValue = schema.default !== undefined ? schema.default : (schema.minimum || 0);
                  setManualInputs({ ...manualInputs, [key]: defaultValue });
                }
              } else {
                const numValue = Number(val);
                // Only enforce minimum if not allowing empty or if user entered a value
                if (!allowEmpty && schema.minimum !== undefined && numValue < schema.minimum) {
                  setManualInputs({ ...manualInputs, [key]: schema.minimum });
                } else {
                  setManualInputs({ ...manualInputs, [key]: numValue });
                }
              }
            }}
            min={allowEmpty ? undefined : schema.minimum}
            max={schema.maximum}
            placeholder={allowEmpty ? 'Leave empty for all' : (schema.default !== undefined ? `Default: ${schema.default}` : '')}
          />
        </div>
      );
    }
    
    // Default to text input
    return (
      <div key={key} className="space-y-2">
        <Label>{schema.title || key}</Label>
        {schema.description && (
          <p className="text-xs text-slate-500">{schema.description}</p>
        )}
        <Input
          type="text"
          value={value || ''}
          onChange={(e) => setManualInputs({ ...manualInputs, [key]: e.target.value })}
          placeholder={schema.example || ''}
        />
      </div>
    );
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'SUCCEEDED':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'FAILED':
        return <XCircle className="w-4 h-4 text-red-600" />;
      case 'RUNNING':
        return <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />;
      case 'ABORTED':
        return <AlertCircle className="w-4 h-4 text-orange-600" />;
      default:
        return <Clock className="w-4 h-4 text-slate-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'SUCCEEDED':
        return 'bg-green-100 text-green-800';
      case 'FAILED':
        return 'bg-red-100 text-red-800';
      case 'RUNNING':
        return 'bg-blue-100 text-blue-800';
      case 'ABORTED':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-slate-100 text-slate-800';
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!actor) {
    return (
      <div className="p-8">
        <div className="text-center">
          <p className="text-slate-500">Actor not found</p>
          <Button onClick={() => navigate(-1)} className="mt-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </div>

        {/* Actor Title */}
        <Card>
          <CardHeader>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <CardTitle className="text-3xl">{actor.title || actor.name}</CardTitle>
                <CardDescription className="text-lg mt-2">
                  {actor.username}/{actor.name}
                </CardDescription>
              </div>
              <Button variant="outline" asChild>
                <a
                  href={`https://apify.com/${actor.username}/${actor.name}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  View on Apify
                </a>
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Stats Row */}
            <div className="flex gap-6 flex-wrap">
              {actor.stats?.averageRating && (
                <div className="flex items-center gap-2">
                  <Star className="w-5 h-5 text-amber-500 fill-amber-500" />
                  <span className="font-semibold text-lg">{actor.stats.averageRating.toFixed(1)}</span>
                  {actor.stats.ratingsCount && (
                    <span className="text-slate-500 text-sm">
                      ({actor.stats.ratingsCount} {actor.stats.ratingsCount === 1 ? 'review' : 'reviews'})
                    </span>
                  )}
                </div>
              )}
              
              <div className="flex items-center gap-2 text-slate-600">
                <TrendingUp className="w-5 h-5" />
                <span>{actor.stats?.totalRuns?.toLocaleString() || 0} runs</span>
              </div>

              <div className="flex items-center gap-2 text-slate-600">
                <Users className="w-5 h-5" />
                <span>{actor.stats?.totalUsers?.toLocaleString() || 0} users</span>
              </div>
            </div>

            {/* Categories */}
            {actor.categories && actor.categories.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {actor.categories.map((cat) => (
                  <Badge key={cat} variant="secondary">
                    {cat}
                  </Badge>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Main Tabs */}
        <Tabs defaultValue="input" className="w-full">
          <TabsList className="grid w-full grid-cols-7">
            <TabsTrigger value="input">Input</TabsTrigger>
            <TabsTrigger value="runs">Runs</TabsTrigger>
            <TabsTrigger value="builds">Builds</TabsTrigger>
            <TabsTrigger value="monitoring">Monitoring</TabsTrigger>
            <TabsTrigger value="issues">Issues</TabsTrigger>
            <TabsTrigger value="tasks">Saved Tasks</TabsTrigger>
            <TabsTrigger value="info">Information</TabsTrigger>
          </TabsList>

          {/* Input Tab */}
          <TabsContent value="input" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Configure Input</CardTitle>
                    <CardDescription>Set up the actor's input parameters</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={handleSaveInput}>
                      {isSaved ? <BookmarkCheck className="w-4 h-4 mr-2" /> : <Bookmark className="w-4 h-4 mr-2" />}
                      {isSaved ? 'Saved' : 'Save'}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setSaveTaskDialogOpen(true)}>
                      <Folder className="w-4 h-4 mr-2" />
                      Save as Task
                    </Button>
                    <Button onClick={handleRunActor} disabled={running}>
                      {running ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Running...
                        </>
                      ) : (
                        <>
                          <Play className="w-4 h-4 mr-2" />
                          Run
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Mode Toggle */}
                <div className="flex items-center gap-4 border-b pb-4">
                  <Button
                    variant={inputMode === "manual" ? "default" : "outline"}
                    size="sm"
                    onClick={() => handleModeSwitch("manual")}
                  >
                    Manual
                  </Button>
                  <Button
                    variant={inputMode === "json" ? "default" : "outline"}
                    size="sm"
                    onClick={() => handleModeSwitch("json")}
                  >
                    JSON
                  </Button>
                </div>

                {/* Manual Input Mode */}
                {inputMode === "manual" && (
                  <div className="space-y-4">
                    {!inputSchema ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin text-blue-600 mr-2" />
                        <span className="text-slate-600">Loading input schema...</span>
                      </div>
                    ) : !inputSchema.properties || Object.keys(inputSchema.properties).length === 0 ? (
                      <div className="text-center py-8 text-slate-500">
                        No input parameters available for this actor
                      </div>
                    ) : (
                      Object.keys(inputSchema.properties).map(key => 
                        renderManualInput(key, inputSchema.properties[key])
                      )
                    )}
                  </div>
                )}

                {/* JSON Input Mode */}
                {inputMode === "json" && (
                  <div className="space-y-2">
                    <Label>JSON Input</Label>
                    <Textarea
                      value={jsonInput}
                      onChange={(e) => setJsonInput(e.target.value)}
                      className="font-mono text-sm min-h-[400px]"
                      placeholder="{}"
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Runs Tab */}
          <TabsContent value="runs" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Run History</CardTitle>
                <CardDescription>View all past executions of this actor</CardDescription>
              </CardHeader>
              <CardContent>
                {loadingRuns ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                  </div>
                ) : runs.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    No runs yet. Run the actor to see results here.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {runs.map((run) => (
                      <div
                        key={run.id}
                        className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50 cursor-pointer"
                        onClick={() => fetchRunResults(run.id)}
                      >
                        <div className="flex items-center gap-3">
                          {getStatusIcon(run.status)}
                          <div>
                            <p className="font-medium">{run.id}</p>
                            <p className="text-sm text-slate-500">
                              {new Date(run.startedAt).toLocaleString()}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge className={getStatusColor(run.status)}>
                            {run.status}
                          </Badge>
                          {run.stats?.computeUnits && (
                            <span className="text-sm text-slate-600">
                              {run.stats.computeUnits} CU
                            </span>
                          )}
                          <Button size="sm" variant="outline">
                            <Eye className="w-4 h-4 mr-2" />
                            View
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Run Results */}
            {runResults && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Results</CardTitle>
                      <CardDescription>
                        {runResults.count} items extracted
                        {runSavedInLeads[currentRun]?.saved && (
                          <span className="ml-2 text-sm text-green-600 font-semibold">
                            ✓ Already in Leads: "{runSavedInLeads[currentRun].name}"
                          </span>
                        )}
                      </CardDescription>
                    </div>
                    <div className="flex gap-2">
                      {!runSavedInLeads[currentRun]?.saved && (
                        <Button 
                          size="sm" 
                          onClick={() => setShowSaveDialog(true)}
                          className="bg-blue-600 hover:bg-blue-700"
                        >
                          <Database className="w-4 h-4 mr-2" />
                          Save to Leads
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => handleDownloadResults(currentRun, 'json')}>
                        <Download className="w-4 h-4 mr-2" />
                        JSON
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleDownloadResults(currentRun, 'csv')}>
                        <Download className="w-4 h-4 mr-2" />
                        CSV
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="max-h-[600px] overflow-auto">
                  {renderResultsAsTable(runResults.items)}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Builds Tab */}
          <TabsContent value="builds" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Latest Builds</CardTitle>
                <CardDescription>Recent builds of this actor</CardDescription>
              </CardHeader>
              <CardContent>
                {builds.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    No builds available
                  </div>
                ) : (
                  <div className="space-y-3">
                    {builds.map((build) => (
                      <div
                        key={build.id}
                        className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50"
                      >
                        <div className="flex items-center gap-3">
                          <GitBranch className="w-5 h-5 text-slate-600" />
                          <div>
                            <p className="font-medium">{build.buildNumber || build.id}</p>
                            <p className="text-sm text-slate-500">
                              {new Date(build.startedAt).toLocaleString()}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge className={getStatusColor(build.status)}>
                            {build.status}
                          </Badge>
                          <Button size="sm" variant="outline">
                            <Eye className="w-4 h-4 mr-2" />
                            View
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Monitoring Tab */}
          <TabsContent value="monitoring" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Performance Monitoring</CardTitle>
                <CardDescription>Track actor performance and set up alerts</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="border rounded-lg p-4">
                    <p className="text-sm text-slate-500 mb-1">Avg Duration</p>
                    <p className="text-2xl font-bold">
                      {runs.length > 0 
                        ? Math.round(runs.reduce((sum, r) => sum + (r.stats?.runTimeSecs || 0), 0) / runs.length)
                        : 0}s
                    </p>
                  </div>
                  <div className="border rounded-lg p-4">
                    <p className="text-sm text-slate-500 mb-1">Success Rate</p>
                    <p className="text-2xl font-bold text-green-600">
                      {runs.length > 0
                        ? Math.round((runs.filter(r => r.status === 'SUCCEEDED').length / runs.length) * 100)
                        : 0}%
                    </p>
                  </div>
                  <div className="border rounded-lg p-4">
                    <p className="text-sm text-slate-500 mb-1">Total Runs</p>
                    <p className="text-2xl font-bold">{runs.length}</p>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold">Alerts</h3>
                    <Button size="sm" variant="outline">
                      <Bell className="w-4 h-4 mr-2" />
                      Add Alert
                    </Button>
                  </div>
                  <p className="text-sm text-slate-500">
                    No alerts configured. Set up alerts to monitor actor performance.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Issues Tab */}
          <TabsContent value="issues" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Issues</CardTitle>
                    <CardDescription>Track and report issues with this actor</CardDescription>
                  </div>
                  <Button onClick={() => setNewIssueDialogOpen(true)}>
                    <Bug className="w-4 h-4 mr-2" />
                    New Issue
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {issues.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    No issues reported
                  </div>
                ) : (
                  <div className="space-y-3">
                    {issues.map((issue) => (
                      <div
                        key={issue.id}
                        className="flex items-start justify-between p-4 border rounded-lg hover:bg-slate-50"
                      >
                        <div className="flex items-start gap-3">
                          <Bug className="w-5 h-5 text-red-600 mt-1" />
                          <div>
                            <p className="font-medium">{issue.title}</p>
                            <p className="text-sm text-slate-500 mt-1">
                              {issue.description}
                            </p>
                            <p className="text-xs text-slate-400 mt-2">
                              Created {new Date(issue.createdAt).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <Badge variant={issue.status === 'open' ? 'destructive' : 'secondary'}>
                          {issue.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Saved Tasks Tab */}
          <TabsContent value="tasks" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Saved Task Configurations</CardTitle>
                <CardDescription>Quickly run actors with pre-configured inputs</CardDescription>
              </CardHeader>
              <CardContent>
                {savedTasks.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    No saved tasks. Use "Save as Task" button to create one.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {savedTasks.map((task) => (
                      <div
                        key={task.id}
                        className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50"
                      >
                        <div className="flex items-center gap-3">
                          <Folder className="w-5 h-5 text-blue-600" />
                          <div>
                            <p className="font-medium">{task.name}</p>
                            <p className="text-sm text-slate-500">
                              Saved {new Date(task.createdAt).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => handleRunTask(task)}>
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Load
                          </Button>
                          <Button size="sm" onClick={() => {
                            handleRunTask(task);
                            handleRunActor();
                          }}>
                            <Play className="w-4 h-4 mr-2" />
                            Run
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Information Tab */}
          <TabsContent value="info" className="space-y-4">
            {/* Sub-tabs for Information */}
            <Tabs defaultValue="description" className="w-full">
              <TabsList>
                <TabsTrigger value="description">Description</TabsTrigger>
                <TabsTrigger value="readme">README</TabsTrigger>
                <TabsTrigger value="changelog">Changelog</TabsTrigger>
                <TabsTrigger value="stats">Statistics</TabsTrigger>
              </TabsList>

              <TabsContent value="description">
                <Card>
                  <CardHeader>
                    <CardTitle>Description</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-700 whitespace-pre-wrap">
                      {actor.description || "No description available"}
                    </p>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="readme">
                <Card>
                  <CardHeader>
                    <CardTitle>README</CardTitle>
                  </CardHeader>
                  <CardContent className="prose max-w-none">
                    {actor.readme ? (
                      <ReactMarkdown>{actor.readme}</ReactMarkdown>
                    ) : (
                      <p className="text-slate-600">
                        View the complete README on the{" "}
                        <a
                          href={`https://apify.com/${actor.username}/${actor.name}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline"
                        >
                          Apify actor page
                        </a>
                      </p>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="changelog">
                <Card>
                  <CardHeader>
                    <CardTitle>Changelog</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-600">
                      View version history and changelog on the{" "}
                      <a
                        href={`https://apify.com/${actor.username}/${actor.name}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        Apify actor page
                      </a>
                    </p>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="stats">
                <Card>
                  <CardHeader>
                    <CardTitle>Statistics</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-sm text-slate-500">Total Runs</p>
                        <p className="text-2xl font-bold">{actor.stats.totalRuns?.toLocaleString() || 0}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Total Users</p>
                        <p className="text-2xl font-bold">{actor.stats.totalUsers?.toLocaleString() || 0}</p>
                      </div>
                      {actor.stats.averageRating && (
                        <div>
                          <p className="text-sm text-slate-500">Average Rating</p>
                          <p className="text-2xl font-bold">{actor.stats.averageRating.toFixed(1)} ⭐</p>
                        </div>
                      )}
                      {actor.stats.totalBuilds && (
                        <div>
                          <p className="text-sm text-slate-500">Total Builds</p>
                          <p className="text-2xl font-bold">{actor.stats.totalBuilds}</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </TabsContent>
        </Tabs>

        {/* Save Task Dialog */}
        <Dialog open={saveTaskDialogOpen} onOpenChange={setSaveTaskDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Save as Task</DialogTitle>
              <DialogDescription>
                Give this configuration a name to save it for later use
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Task Name</Label>
                <Input
                  value={taskName}
                  onChange={(e) => setTaskName(e.target.value)}
                  placeholder="e.g., Weekly scrape"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSaveTaskDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveAsTask} disabled={!taskName.trim()}>
                <Save className="w-4 h-4 mr-2" />
                Save Task
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* New Issue Dialog */}
        <Dialog open={newIssueDialogOpen} onOpenChange={setNewIssueDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Report an Issue</DialogTitle>
              <DialogDescription>
                Describe the problem you're experiencing with this actor
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Issue Title</Label>
                <Input
                  value={issueTitle}
                  onChange={(e) => setIssueTitle(e.target.value)}
                  placeholder="Brief description of the issue"
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={issueDescription}
                  onChange={(e) => setIssueDescription(e.target.value)}
                  placeholder="Provide details about the issue..."
                  rows={4}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setNewIssueDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateIssue} disabled={!issueTitle.trim()}>
                <Bug className="w-4 h-4 mr-2" />
                Create Issue
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Save to Leads Dialog */}
        <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Save to Leads</DialogTitle>
              <DialogDescription>
                Give this lead list a name. You'll be able to access it from AI Task → Leads.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="lead-name">Lead List Name</Label>
                <Input
                  id="lead-name"
                  placeholder="e.g., NYC Restaurants, LA Hotels, Miami Cafes"
                  value={leadName}
                  onChange={(e) => setLeadName(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSaveToLeads()}
                  className="mt-2"
                />
              </div>
              <div className="text-sm text-slate-600 bg-slate-50 p-3 rounded">
                <strong>What will be saved:</strong>
                <ul className="mt-2 space-y-1 list-disc list-inside">
                  <li>{runResults?.count || 0} leads</li>
                  <li>{visibleColumns.length} visible columns</li>
                  <li>Current column order and widths</li>
                  <li>Scrape date: {new Date().toLocaleString()}</li>
                </ul>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSaveDialog(false)} disabled={savingLead}>
                Cancel
              </Button>
              <Button onClick={handleSaveToLeads} disabled={!leadName.trim() || savingLead}>
                {savingLead ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Database className="w-4 h-4 mr-2" />
                    Save to Leads
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
